/** VMS platform standard date display locale (dd/mm/yyyy). */
export const VMS_DATE_LOCALE = 'en-GB';

export function parseVmsDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '—') return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = new Date(`${trimmed}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{8}$/.test(trimmed)) {
    const y = trimmed.slice(0, 4);
    const m = trimmed.slice(4, 6);
    const day = trimmed.slice(6, 8);
    const asYmd = new Date(`${y}-${m}-${day}T12:00:00`);
    if (
      !Number.isNaN(asYmd.getTime()) &&
      asYmd.getFullYear() === Number(y) &&
      asYmd.getMonth() + 1 === Number(m) &&
      asYmd.getDate() === Number(day)
    ) {
      return asYmd;
    }

    const dd = trimmed.slice(0, 2);
    const mm = trimmed.slice(2, 4);
    const yy = trimmed.slice(4, 8);
    const asDmy = new Date(`${yy}-${mm}-${dd}T12:00:00`);
    if (
      !Number.isNaN(asDmy.getTime()) &&
      asDmy.getFullYear() === Number(yy) &&
      asDmy.getMonth() + 1 === Number(mm) &&
      asDmy.getDate() === Number(dd)
    ) {
      return asDmy;
    }

    return null;
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(trimmed);
  if (slashMatch) {
    let dd = slashMatch[1];
    let mm = slashMatch[2];
    const yy = slashMatch[3];
    const y = yy.length === 2 ? `20${yy}` : yy;
    const a = parseInt(dd, 10);
    const b = parseInt(mm, 10);
    if (a <= 12 && b > 12) {
      [dd, mm] = [mm, dd];
    }
    const d = new Date(`${y}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const dashMatch = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(trimmed);
  if (dashMatch) {
    let dd = dashMatch[1];
    let mm = dashMatch[2];
    const y = dashMatch[3];
    const a = parseInt(dd, 10);
    const b = parseInt(mm, 10);
    if (a <= 12 && b > 12) {
      [dd, mm] = [mm, dd];
    }
    const d = new Date(`${y}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const normalized =
    trimmed.includes(' ') && !trimmed.includes('T') ? trimmed.replace(' ', 'T') : trimmed;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** HTML `type="date"` bounds — keeps the year segment at exactly 4 digits. */
export const VMS_DATE_INPUT_MIN = '1000-01-01';
export const VMS_DATE_INPUT_MAX = '9999-12-31';

/**
 * Truncate an over-long year on native date values (e.g. `20260-01-15` → `2026-01-15`).
 * Leaves empty / already-valid values unchanged.
 */
export function clampVmsDateInputYear(value: string | null | undefined): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const match = /^(\d{5,})(-.*)?$/.exec(trimmed);
  if (!match) return trimmed;
  const year = match[1].slice(0, 4);
  const rest = match[2] ?? '';
  return `${year}${rest}`;
}

/** True when `value` is a strict YYYY-MM-DD calendar date (HTML `type="date"`). */
export function isValidVmsDateInputValue(value: string | null | undefined): boolean {
  const trimmed = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const yNum = parseInt(trimmed.slice(0, 4), 10);
  if (yNum < 1000 || yNum > 9999) return false;
  const d = parseVmsDate(trimmed);
  if (!d) return false;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}` === trimmed;
}

/** Stored/display date → YYYY-MM-DD for HTML `type="date"` inputs. */
export function toVmsDateInputValue(value: string | Date | null | undefined): string {
  const d = parseVmsDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** dd/mm/yyyy — primary VMS display format. */
export function formatVmsDisplayDate(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const d = parseVmsDate(value);
  if (!d) {
    const raw = value != null ? String(value).trim() : '';
    if (raw && raw !== '—') return raw;
    return fallback;
  }
  return d.toLocaleDateString(VMS_DATE_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** dd-mm-yyyy — alternate VMS display format (register fields). */
export function formatVmsDisplayDateDash(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const formatted = formatVmsDisplayDate(value, '');
  if (!formatted) {
    const raw = value != null ? String(value).trim() : '';
    if (raw && raw !== '—') return raw;
    return fallback;
  }
  return formatted.replace(/\//g, '-');
}

/** dd/mm/yyyy, HH:mm */
export function formatVmsDisplayDateTime(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const d = parseVmsDate(value);
  if (!d) return fallback;
  return d.toLocaleString(VMS_DATE_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** e.g. 19 May 2026 */
export function formatVmsDisplayDateMedium(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const d = parseVmsDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(VMS_DATE_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** e.g. May 2028 — periodic review next month column */
export function formatVmsMonthYear(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const d = parseVmsDate(value);
  if (!d) {
    const raw = value != null ? String(value).trim() : '';
    return raw || fallback;
  }
  return d.toLocaleDateString(VMS_DATE_LOCALE, { month: 'long', year: 'numeric' });
}

/** yyyyMMdd, yyyy-mm-dd, dd/mm/yyyy, or dd-mm-yyyy → dd-mm-yyyy for register read-only display. */
export function formatVmsStoredYmd(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '—';
  const parsed = parseVmsDate(s);
  if (parsed) return formatVmsDisplayDateDash(parsed);
  return s;
}
